import { FC, useEffect, useState } from 'react'
import {
	Form,
	Header,
	SpaceBetween,
	Box,
	Button,
	Container
} from '@cloudscape-design/components'
import FormTokenGroup from '../../ControlledFormInputs/InputTokenGroup'
import FormSelect from '../../ControlledFormInputs/Select'
import { Control, FieldValues, UseFormSetError } from 'react-hook-form'
import CONSTANTS from '../../../constants'

export interface ConnectivityAnalyzerConfigureFormProps {
	control: Control<FieldValues, any>
	formValues: Record<any, any>
	setFormValues: (fieldName: string, value: any) => void
	setError: UseFormSetError<Record<string,any>>
	isLoading: boolean
	submitForm: () => void
	cancelForm: () => void
	existingViewers?: Array<string>
	existingAnalysisFrequency?: 'daily' | 'weekly' | 'monthly' | null
	platformName: string
	projectName: string
}

const ConnectivityAnalyzerConfigureForm: FC<
	ConnectivityAnalyzerConfigureFormProps
> = ({
	control,
	formValues,
	setFormValues,
	setError,
	isLoading,
	submitForm,
	cancelForm,
	existingViewers,
	existingAnalysisFrequency,
	platformName,
	projectName
}) => {
  let viewers: Array<{label: string, dismissLabel: string}> = formValues.viewerEmails.map((email: string) => ({ label: email, dismissLabel: `Remove item ${email}` }))

	if (existingViewers !== undefined) {
		viewers = existingViewers.map((email) => ({ label: email, dismissLabel: `Remove item ${email}` }))
	}

	const [disableSubmit, setDisableSubmit] = useState(true)

	// For Disabling the submit button if there are no changes in the viewer emails field
	useEffect(() => {
		if (existingViewers !== undefined) {
			if (formValues.viewerEmails.length !== existingViewers.length) {
				setDisableSubmit(false)
			}
			else {
        formValues.viewerEmails.forEach((e: {label: string}, index: number) => {
      		if (e.label !== existingViewers[index]) {
            setDisableSubmit(false)
      		} else {
        		setDisableSubmit(true)
      		}
        })
			}
		}
	}, [formValues.viewerEmails, setDisableSubmit, existingViewers])

	// For disabling ths submit button if there are no changes in analysisFrequency, except if pre-enrolled
	useEffect(() => {
		if (existingAnalysisFrequency !== undefined) {
			if (formValues.analysisFrequency === existingAnalysisFrequency) {
				setDisableSubmit(true)
			}
			else {
				setDisableSubmit(false)
			}
		}
		else {
			setDisableSubmit(false)
		}
	}, [formValues.analysisFrequency, existingAnalysisFrequency, setDisableSubmit])

  const _submitButtonClicked = () => {
    console.log('FormValues in ConnectivityAnalyzerConfigureForm', formValues)
    if (disableSubmit === false) {
      if (formValues.viewerEmails.length < 1) {
  			setError('viewerEmails', {type: 'custom', message: 'Need atleast one email'})
  		}
  		else {
  			submitForm()
  		}
    }
	}

	return (
		<Form
			header={
				<Header variant="h1">
					Configure Connectivity Analyzer ({platformName}/{projectName})
				</Header>
			}
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Box>
							<Button
								variant="primary"
								onClick={_submitButtonClicked}
								disabled={disableSubmit}
								loading={isLoading}
							>
								{existingViewers !== undefined ? "Save" : "Submit"}
							</Button>
						<Button variant="link" onClick={cancelForm} disabled={isLoading}>
							Cancel
						</Button>
					</Box>
				</SpaceBetween>
			}
		>
			<Container>
				<SpaceBetween direction="vertical" size="l">
					<FormTokenGroup
						label="Viewer Emails"
						name="viewerEmails"
						control={control}
						items={viewers}
						placeholder="Type Globe Email and press 'Enter'"
						setFormValues={setFormValues}
					/>
					<FormSelect
						label="Analysis Frequency"
						name="analysisFrequency"
						control={control}
						placeholder="Daily, Weekly, Monthly or None"
						options={CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS}
						setFormValues={setFormValues}
					/>
				</SpaceBetween>
			</Container>
		</Form>
	)
}

export default ConnectivityAnalyzerConfigureForm
